import { Component, DestroyRef, OnInit, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { RadioButtonModule } from 'primeng/radiobutton';
import { TextareaModule } from 'primeng/textarea';
import { LocalizationService } from '../../core/localization/localization.service';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import {
  CreatePlatformPaymentTermPayload,
  FinanceApiService,
  PlatformPaymentTermRecord,
  UpdatePlatformPaymentTermPayload
} from '../finance/finance-api.service';

@Component({
  standalone: true,
  selector: 'ain-platform-payment-term-detail',
  imports: [
    AinPageComponent,
    AinStateComponent,
    RouterLink,
    FormsModule,
    ButtonModule,
    InputTextModule,
    RadioButtonModule,
    TextareaModule,
    InfoHintComponent
  ],
  template: `
    <ain-page
      [eyebrow]="t('platform.eyebrow')"
      [title]="isCreateMode() ? t('platform.paymentTerms.form.createTitle') : t('platform.paymentTerms.form.editTitle')"
      [description]="t('platform.paymentTerms.detailDescription')">
      <ain-info-hint
        page-title-addon
        [titleKey]="'partyHub.help.paymentTermDetail.title'"
        [descriptionKey]="'partyHub.help.paymentTermDetail.description'"
        [bulletKeys]="[
          'partyHub.help.paymentTermDetail.bullets.netDays',
          'partyHub.help.paymentTermDetail.bullets.prepayment',
          'partyHub.help.paymentTermDetail.bullets.status'
        ]" />

      @if (loading()) {
        <ain-state type="loading" [title]="t('common.loading')" />
      } @else {
        <div class="toolbar">
          <a class="back-link" [routerLink]="['/platform/payment-terms']">{{ t('catalog.action.backToList') }}</a>
          <button pButton type="button" [label]="t('common.save')" (click)="save()" [disabled]="saving()"></button>
        </div>

        <section class="card">
          <div class="grid">
            <label>
              <span>{{ t('platform.paymentTerms.fields.termCode') }}</span>
              <input pInputText [(ngModel)]="form.termCode" [readonly]="!isCreateMode()" />
            </label>
            <label>
              <span>{{ t('platform.paymentTerms.fields.name') }}</span>
              <input pInputText [(ngModel)]="form.name" />
            </label>
            <label class="full">
              <span>{{ t('platform.paymentTerms.fields.description') }}</span>
              <textarea pTextarea rows="3" [(ngModel)]="form.description"></textarea>
            </label>
            <label>
              <span>{{ t('platform.paymentTerms.fields.netDays') }}</span>
              <input pInputText type="number" [(ngModel)]="form.netDays" />
            </label>
            <label>
              <span>{{ t('platform.paymentTerms.fields.prepaymentPercent') }}</span>
              <input pInputText type="number" step="0.01" [(ngModel)]="form.prepaymentPercent" />
            </label>
            @if (!isCreateMode()) {
              <div class="radio-wrapper">
                <span>{{ t('columns.status') }}</span>
                <div class="radio-group">
                  <label class="radio-item">
                    <p-radiobutton name="paymentTermStatus" inputId="payment-term-active" [value]="true" [(ngModel)]="form.isActive" />
                    <span>{{ t('common.yes') }}</span>
                  </label>
                  <label class="radio-item">
                    <p-radiobutton name="paymentTermStatus" inputId="payment-term-inactive" [value]="false" [(ngModel)]="form.isActive" />
                    <span>{{ t('common.no') }}</span>
                  </label>
                </div>
              </div>
            }
          </div>
        </section>

        @if (errorMessage()) {
          <p class="message error">{{ errorMessage() }}</p>
        }
      }
    </ain-page>
  `,
  styles: [`
    .toolbar { margin-bottom: 12px; display: flex; justify-content: space-between; align-items: center; gap: 8px; }
    .back-link { color: var(--ain-color-primary); font-weight: 600; text-decoration: none; }
    .card { border: 1px solid #e5e7eb; border-radius: 12px; background: #fff; padding: 12px; margin-bottom: 12px; }
    .grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(240px, 1fr)); gap: 10px; margin-bottom: 10px; }
    label { display: grid; gap: 6px; }
    .full { grid-column: 1 / -1; }
    .radio-wrapper { display: grid; gap: 6px; }
    .radio-group { display: flex; align-items: center; flex-wrap: wrap; gap: 14px; min-height: 2.5rem; }
    .radio-item { display: inline-flex; align-items: center; gap: 8px; }
    .message.error { color: #b91c1c; }
  `]
})
export class PlatformPaymentTermDetailComponent implements OnInit {
  private readonly api = inject(FinanceApiService);
  private readonly localization = inject(LocalizationService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);

  readonly loading = signal(true);
  readonly saving = signal(false);
  readonly errorMessage = signal('');
  readonly currentTerm = signal<PlatformPaymentTermRecord | null>(null);
  readonly isCreateMode = signal(false);

  form = {
    termCode: '',
    name: '',
    description: '',
    netDays: '' as string | number,
    prepaymentPercent: '' as string | number,
    isActive: true
  };

  ngOnInit(): void {
    this.route.paramMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(params => {
      const paymentTermId = params.get('paymentTermId');
      const isCreate = !paymentTermId || paymentTermId === 'new';
      this.isCreateMode.set(isCreate);
      this.errorMessage.set('');

      if (isCreate) {
        this.currentTerm.set(null);
        this.form = {
          termCode: '',
          name: '',
          description: '',
          netDays: '',
          prepaymentPercent: '',
          isActive: true
        };
        this.loading.set(false);
        return;
      }

      this.loadTerm(paymentTermId);
    });
  }

  save(): void {
    const termCode = this.form.termCode.trim();
    const name = this.form.name.trim();
    if (!termCode || !name) {
      this.errorMessage.set(this.t('common.saveFailed'));
      return;
    }

    this.saving.set(true);
    this.errorMessage.set('');

    const netDays = this.parseOptionalInt(this.form.netDays);
    const prepaymentPercent = this.parseOptionalDecimal(this.form.prepaymentPercent);
    const description = this.form.description.trim() || undefined;

    if (this.isCreateMode()) {
      const payload: CreatePlatformPaymentTermPayload = {
        termCode,
        name,
        description,
        netDays,
        prepaymentPercent
      };
      this.api.createPlatformPaymentTerm(payload).subscribe({
        next: created => {
          this.saving.set(false);
          void this.router.navigate(['/platform/payment-terms', created.id]);
        },
        error: () => {
          this.errorMessage.set(this.t('common.saveFailed'));
          this.saving.set(false);
        }
      });
      return;
    }

    const current = this.currentTerm();
    if (!current) {
      this.errorMessage.set(this.t('common.saveFailed'));
      this.saving.set(false);
      return;
    }

    const payload: UpdatePlatformPaymentTermPayload = {
      name,
      description,
      netDays,
      prepaymentPercent,
      isActive: this.form.isActive
    };
    this.api.updatePlatformPaymentTerm(current.id, payload).subscribe({
      next: updated => {
        this.currentTerm.set(updated);
        this.saving.set(false);
      },
      error: () => {
        this.errorMessage.set(this.t('common.saveFailed'));
        this.saving.set(false);
      }
    });
  }

  t(key: string): string {
    return this.localization.translate(key);
  }

  private loadTerm(paymentTermId: string): void {
    this.loading.set(true);
    this.api.getPlatformPaymentTerm(paymentTermId).subscribe({
      next: term => {
        this.currentTerm.set(term);
        this.form = {
          termCode: term.termCode,
          name: term.name,
          description: term.description ?? '',
          netDays: term.netDays ?? '',
          prepaymentPercent: term.prepaymentPercent ?? '',
          isActive: term.isActive
        };
        this.loading.set(false);
      },
      error: () => {
        this.errorMessage.set(this.t('common.loadFailed'));
        this.loading.set(false);
      }
    });
  }

  private parseOptionalInt(value: string | number): number | undefined {
    if (value === '' || value === null || value === undefined) {
      return undefined;
    }
    const parsed = typeof value === 'number' ? value : Number.parseInt(`${value}`.trim(), 10);
    return Number.isFinite(parsed) ? parsed : undefined;
  }

  private parseOptionalDecimal(value: string | number): number | undefined {
    if (value === '' || value === null || value === undefined) {
      return undefined;
    }
    const parsed = typeof value === 'number' ? value : Number.parseFloat(`${value}`.trim());
    return Number.isFinite(parsed) ? parsed : undefined;
  }
}
